import { Link } from 'react-router-dom';
import { useState } from 'react';
import { ChevronDown, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

const faqItems = [
  {
    question: 'Como funciona a teleconsulta?',
    answer: 'Após escolher seu médico e horário, você acessa uma sala de espera virtual. No horário marcado, o médico inicia a videochamada. Você precisa apenas de uma câmera e microfone no celular ou computador.',
  },
  {
    question: 'Como agendo uma consulta?',
    answer: 'Na página inicial, pesquise pelo médico ou especialidade desejada. Selecione o profissional, escolha o horário disponível e confirme o pagamento. Você receberá um e-mail com o link da sala de espera.',
  },
  {
    question: 'Como funciona o pagamento?',
    answer: 'O pagamento é feito de forma segura pela nossa plataforma. Aceitamos cartão de crédito, PIX e boleto. O valor é liberado ao médico apenas após a conclusão da consulta.',
  },
  {
    question: 'Como solicito reembolso?',
    answer: 'Para solicitar reembolso, acesse "Meus Agendamentos" e selecione a consulta desejada. Clique em "Solicitar Reembolso" e preencha o formulário. O processo leva de 5 a 10 dias úteis.',
  },
  {
    question: 'Como funciona a consulta domiciliar?',
    answer: 'Na página inicial, selecione a opção "Consulta em casa". Informe seu endereço e preferência de horário. Um médico próximo à sua região entrará em contato para confirmar a visita.',
  },
  {
    question: 'Como entro em contato com suporte?',
    answer: 'Você pode entrar em contato pelo e-mail contato@medikei.com.br ou pelo telefone 0800 123 4567, de segunda a sexta, das 8h às 18h. Também oferecemos suporte pelo chat da plataforma.',
  },
];

export function HelpPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="container-landing flex items-center justify-between py-4">
          <Link to="/" className="font-bold text-xl text-primary">
            <img src="/logo.png" alt="Medikei" className="h-10 w-auto" decoding="async" />
          </Link>
          <Link to="/">
            <Button variant="secondary" size="sm">Voltar ao site</Button>
          </Link>
        </div>
      </header>

      <main className="py-16 md:py-24">
        <div className="container-landing max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Central de Ajuda</h1>
            <p className="text-muted-foreground text-lg">
              Encontre respostas para suas dúvidas ou fale com nosso suporte
            </p>
          </div>

          <div className="space-y-3 mb-16">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-border overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/30 transition-colors"
                  aria-expanded={openIndex === index}
                >
                  <span className="font-medium pr-4">{item.question}</span>
                  <ChevronDown
                    className={cn(
                      'w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform',
                      openIndex === index && 'rotate-180'
                    )}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-5 text-muted-foreground">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-muted/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-semibold mb-4">Não encontrou o que procurava?</h2>
            <p className="text-muted-foreground mb-6">
              Nossa equipe está pronta para ajudar você. Entre em contato pelos canais abaixo:
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:contato@medikei.com.br"
                className="flex items-center gap-2 text-primary font-semibold hover:underline"
              >
                <Mail className="w-5 h-5" />
                contato@medikei.com.br
              </a>
              <span className="hidden sm:block text-muted-foreground">|</span>
              <a
                href="tel:08001234567"
                className="flex items-center gap-2 text-primary font-semibold hover:underline"
              >
                <Phone className="w-5 h-5" />
                0800 123 4567
              </a>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Horário de atendimento: Segunda a sexta, das 8h às 18h
            </p>
          </div>
        </div>
      </main>

      <footer className="border-t border-border py-8 bg-muted/30">
        <div className="container-landing max-w-3xl mx-auto text-center text-sm text-muted-foreground">
          © 2026 Medikei Saúde. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
